/**
 * OpenUp Spaces admin API client — the contract OI-24710's UI
 * defines for the backend (OI-24709 / OI-24711 / OI-24713 / OI-24714).
 *
 * Endpoints live under /api/v1/convene/*, cookie-authed, gated by
 * CustomerSuccessPolicy. Calls go to the real API unless a developer opts in to
 * the typed in-app mock (spaces.mock.ts) with REACT_APP_SPACES_USE_MOCK ===
 * "true" in .env.development.local. Opt-in rather than opt-out so a missing or
 * mistyped flag surfaces a real error instead of fabricated data.
 */
import { isAxiosError } from "axios";
import apiClient from "@src/lib/apiClient";
import { env } from "@src/env";
import {
  SpaceEvent,
  SpaceEventsOverview,
  CreateSpaceEventError,
  CreateSpaceEventRequest,
  Employer,
  HostableExpert,
  EventCancellationResult,
  Invite,
  InviteLink,
  SessionAttendance,
  UpdateSessionRequest,
  UpdateSpaceEventRequest,
} from "@src/models/spaces";
import { SpacesApiError } from "@src/services/spacesError";
import {
  mockCreateEvent,
  mockCreateInviteLink,
  mockCreateInvites,
  mockGetEmployers,
  mockGetEvent,
  mockGetEvents,
  mockGetHostableExperts,
  mockGetInviteLink,
  mockGetInvites,
  mockGetSessionAttendance,
  mockRevokeInviteLink,
  mockCancelEvent,
  mockCancelSession,
  mockUpdateEvent,
  mockUpdateSession,
} from "@src/services/spaces.mock";

// Opt-in, not opt-out. With `!== "false"` every misconfiguration — unset, "FALSE", "0", a typo —
// served fabricated data: a scheduler saw "created, now invite people" for an event that was never
// persisted, with nothing on screen to say so. Failing toward the real API turns those same
// mistakes into visible errors instead of silent fiction.
const isMockEnabled = (): boolean => env.REACT_APP_SPACES_USE_MOCK === "true";

/** Reconstructs a structured SpacesApiError from a backend error body. */
const toSpacesError = (error: unknown): SpacesApiError => {
  if (error instanceof SpacesApiError) {
    return error;
  }
  if (isAxiosError(error)) {
    const detail = error.response?.data as
      | Partial<CreateSpaceEventError>
      | undefined;
    if (detail?.code) {
      return new SpacesApiError({
        code: detail.code,
        message: detail.message ?? error.message,
        sessionIndex: detail.sessionIndex,
        email: detail.email,
        registeredCount: detail.registeredCount,
      });
    }
  }
  // Anything that arrives without a structured code never got a verdict from the backend: a 500,
  // a timeout, a dropped connection, a CORS failure. Reporting those as VALIDATION told the
  // scheduler their input was wrong when the server was simply unreachable. The original error is
  // kept as `cause` because this app initialises no telemetry — the browser console is the only
  // place a failure leaves a trace.
  return new SpacesApiError(
    {
      code: "TRANSPORT",
      message:
        "We couldn't reach the server. Please check your connection and try again.",
    },
    { cause: error },
  );
};

export const getEmployers = async (): Promise<Employer[]> => {
  if (isMockEnabled()) return mockGetEmployers();
  const { data } = await apiClient.get<Employer[]>(
    "/v1/convene/employers?active=true",
  );
  return data;
};

export const getHostableExperts = async (): Promise<HostableExpert[]> => {
  if (isMockEnabled()) return mockGetHostableExperts();
  const { data } = await apiClient.get<HostableExpert[]>(
    "/v1/convene/experts?hostable=true",
  );
  return data;
};

export const createEvent = async (
  request: CreateSpaceEventRequest,
): Promise<SpaceEvent> => {
  try {
    if (isMockEnabled()) return await mockCreateEvent(request);
    const { data } = await apiClient.post<SpaceEvent>(
      "/v1/convene/events",
      request,
    );
    return data;
  } catch (error) {
    throw toSpacesError(error);
  }
};

export const getEvents = async (): Promise<SpaceEventsOverview> => {
  if (isMockEnabled()) return mockGetEvents();
  const { data } =
    await apiClient.get<SpaceEventsOverview>("/v1/convene/events");
  return data;
};

// OI-25024 non-reschedule edits: silent (no attendee email), nothing written
// to Zoom. Date/time and expert changes are the reschedule slice.
export const updateEvent = async (
  id: string,
  request: UpdateSpaceEventRequest,
): Promise<void> => {
  try {
    if (isMockEnabled()) return await mockUpdateEvent(id, request);
    await apiClient.patch(`/v1/convene/events/${id}`, request);
  } catch (error) {
    throw toSpacesError(error);
  }
};

export const updateSession = async (
  sessionId: string,
  request: UpdateSessionRequest,
): Promise<void> => {
  try {
    if (isMockEnabled()) return await mockUpdateSession(sessionId, request);
    await apiClient.patch(`/v1/convene/sessions/${sessionId}`, request);
  } catch (error) {
    throw toSpacesError(error);
  }
};

// OI-24717 cancellation: marks sessions CancelledByOpenUp, deletes the Zoom
// object, and emails registered attendees (Customer.io template 176).
export const cancelSession = async (
  sessionId: string,
  reason: string | null,
): Promise<void> => {
  try {
    if (isMockEnabled()) return await mockCancelSession(sessionId);
    await apiClient.post(`/v1/convene/sessions/${sessionId}/cancel`, {
      reason,
    });
  } catch (error) {
    throw toSpacesError(error);
  }
};

export const cancelEvent = async (
  eventId: string,
  reason: string | null,
): Promise<EventCancellationResult> => {
  try {
    if (isMockEnabled()) return await mockCancelEvent(eventId);
    const { data } = await apiClient.post<EventCancellationResult>(
      `/v1/convene/events/${eventId}/cancel`,
      { reason },
    );
    return data;
  } catch (error) {
    throw toSpacesError(error);
  }
};

export const getEvent = async (id: string): Promise<SpaceEvent> => {
  if (isMockEnabled()) return mockGetEvent(id);
  const { data } = await apiClient.get<SpaceEvent>(`/v1/convene/events/${id}`);
  return data;
};

// Invites and the shareable link are per SESSION (the Space grain) — each
// session has its own invitee list + link, and registration is to that
// specific session (db_design.md §3B/§4.8, schema ADR §3.5).
export const getInvites = async (sessionId: string): Promise<Invite[]> => {
  if (isMockEnabled()) return mockGetInvites(sessionId);
  const { data } = await apiClient.get<Invite[]>(
    `/v1/convene/sessions/${sessionId}/invites`,
  );
  return data;
};

export const createInvites = async (
  sessionId: string,
  emails: string[],
): Promise<Invite[]> => {
  if (isMockEnabled()) return mockCreateInvites(sessionId, emails);
  const { data } = await apiClient.post<Invite[]>(
    `/v1/convene/sessions/${sessionId}/invites`,
    { emails },
  );
  return data;
};

export const getInviteLink = async (
  sessionId: string,
): Promise<InviteLink | null> => {
  if (isMockEnabled()) return mockGetInviteLink(sessionId);
  const { data } = await apiClient.get<InviteLink | null>(
    `/v1/convene/sessions/${sessionId}/invite-link`,
  );
  return data;
};

export const createInviteLink = async (
  sessionId: string,
  seatCap: number | null,
): Promise<InviteLink> => {
  if (isMockEnabled()) return mockCreateInviteLink(sessionId, seatCap);
  const { data } = await apiClient.post<InviteLink>(
    `/v1/convene/sessions/${sessionId}/invite-link`,
    { seatCap },
  );
  return data;
};

export const revokeInviteLink = async (sessionId: string): Promise<void> => {
  if (isMockEnabled()) return mockRevokeInviteLink(sessionId);
  await apiClient.delete(`/v1/convene/sessions/${sessionId}/invite-link`);
};

export const getSessionAttendance = async (
  sessionId: string,
): Promise<SessionAttendance> => {
  if (isMockEnabled()) return mockGetSessionAttendance(sessionId);
  const { data } = await apiClient.get<SessionAttendance>(
    `/v1/convene/sessions/${sessionId}/attendance`,
  );
  return data;
};
